/**
 * Showing a stored cloud diurnal QC day (Diurnal parity C3, 02-060; docs/specs/cloud/diurnal-qc-cloud.md): the stored
 * chart series back as a `DiurnalQc` the shared figure (`diurnal-figure.ts`, the one Field's screenshot and daily PDF
 * draw) can draw, the stored rows read safely, and the words of the Teslyn Cloud Diurnal QC page and the client's
 * published day.
 */
import type { DiurnalQc, DiurnalSettings } from './diurnal-qc.js';
import { METRIC_LABEL, type DiurnalMetric } from './diurnal-qc.js';
import type {
  CloudQcChart,
  CloudQcCounts,
  CloudQcItem,
  CloudQcSettings,
  CloudQcStationStatus,
} from './diurnal-cloud.js';
import { localParts } from './time-model.js';
import { DEFAULT_QC, DEFAULT_VISIBILITY } from './settings.js';

/** The stored series as the QC the figure draws: every stored reading is inside the trim (it was trimmed before). */
export function qcFromCloudChart(chart: CloudQcChart, settings: CloudQcSettings): DiurnalQc {
  const n = chart.t.length;
  const arr = (k: string): Float64Array | null => {
    const a = chart.m[k];
    if (!a) return null;
    const out = new Float64Array(n);
    for (let i = 0; i < n; i++) out[i] = a[i] ?? NaN;
    return out;
  };
  return {
    n,
    settings: settings as DiurnalSettings,
    tMs: Float64Array.from(chart.t),
    values: Float64Array.from(chart.v),
    plot1: arr('plot1') ?? new Float64Array(n).fill(NaN),
    plot2: arr('plot2') ?? new Float64Array(n).fill(NaN),
    gradient: arr('gradient'),
    pcal: arr('pcal'),
    dor: arr('dor'),
    exceed: Uint8Array.from(chart.x),
    gapAfter: Uint8Array.from(chart.g),
    bad: Uint8Array.from(chart.b),
    // the cloud chart keeps the flags as computed; which bad readings were spikes is not sent (R109)
    spike: new Uint8Array(n),
    trim: { start: 0, end: n, trimmed: false },
    periodMs: chart.period_ms,
  };
}

// ------------------------------------------------------------------------------------------------ stored rows

/** A day as `diurnal_qc_day` (or a published copy) holds it. */
export interface CloudQcDayRow {
  readonly id: string;
  readonly project_id: string;
  readonly station_id: string;
  readonly station_name: string;
  readonly day: string;
  readonly offset_h: number;
  readonly status: CloudQcStationStatus;
  readonly readings: number;
  readonly first_at: string | null;
  readonly last_at: string | null;
  readonly final: boolean;
  readonly computed_at: string;
  readonly settings: CloudQcSettings;
  readonly counts: CloudQcCounts;
  readonly items: readonly CloudQcItem[];
  readonly chart: CloudQcChart;
}

const num = (v: unknown, d = 0): number => (typeof v === 'number' && Number.isFinite(v) ? v : d);
const str = (v: unknown, d = ''): string => (typeof v === 'string' ? v : d);
const obj = (v: unknown): Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
const nums = (v: unknown): number[] => (Array.isArray(v) ? v.map((x) => num(x)) : []);

function readSettings(v: unknown): CloudQcSettings {
  const o = obj(v);
  const show = obj(o.show);
  const out: Record<string, unknown> = {};
  for (const [k, d] of Object.entries(DEFAULT_QC)) out[k] = num(o[k], d);
  return {
    ...(out as unknown as Omit<CloudQcSettings, 'plotMode' | 'show'>),
    plotMode: o.plotMode === 'peakToPeak' ? 'peakToPeak' : 'deviationFromChord',
    show: {
      plot2: show.plot2 === true || (show.plot2 === undefined && DEFAULT_VISIBILITY.plot2),
      gradient: show.gradient === true,
      pcal: show.pcal === true,
      dor: show.dor === true,
    },
  };
}

function readChart(v: unknown): CloudQcChart {
  const o = obj(v);
  const t = nums(o.t);
  const m: Record<string, (number | null)[]> = {};
  for (const [k, a] of Object.entries(obj(o.m)))
    if (Array.isArray(a) && a.length === t.length)
      m[k] = a.map((x) => (typeof x === 'number' && Number.isFinite(x) ? x : null));
  const same = (a: number[]): number[] => (a.length === t.length ? a : t.map(() => 0));
  const v2 = nums(o.v);
  return {
    t,
    v: v2.length === t.length ? v2 : t.map(() => NaN),
    m,
    x: same(nums(o.x)),
    g: same(nums(o.g)),
    b: same(nums(o.b)),
    period_ms: typeof o.period_ms === 'number' ? o.period_ms : null,
  };
}

function readItems(v: unknown): CloudQcItem[] {
  if (!Array.isArray(v)) return [];
  const out: CloudQcItem[] = [];
  for (const x of v) {
    const o = obj(x);
    if (o.kind !== 'exceedance' && o.kind !== 'gap' && o.kind !== 'bad_data') continue;
    const p = obj(o.peak);
    out.push({
      kind: o.kind,
      key: str(o.key),
      start_ms: num(o.start_ms),
      end_ms: num(o.end_ms),
      readings: num(o.readings, 1),
      metrics: Array.isArray(o.metrics) ? o.metrics.filter((s): s is string => typeof s === 'string') : [],
      peak:
        o.peak && typeof p.metric === 'string'
          ? { metric: p.metric, value: num(p.value), threshold: num(p.threshold), excess: num(p.excess) }
          : null,
      title: str(o.title),
      details: str(o.details),
      ongoing: o.ongoing === true,
    });
  }
  return out;
}

/** A stored day row (database JSON) in the shape the pages use; unknown or missing parts become empty. */
export function readCloudQcDay(raw: unknown): CloudQcDayRow {
  const o = obj(raw);
  const result = obj(o.result);
  const c = obj(result.counts);
  const status = o.status === 'online' || o.status === 'offline' ? o.status : 'no_recent';
  return {
    id: str(o.id),
    project_id: str(o.project_id),
    station_id: str(o.station_id),
    station_name: str(o.station_name, 'Base station'),
    day: str(o.day),
    offset_h: num(o.offset_h, DEFAULT_QC.utcOffsetH),
    status,
    readings: num(o.readings),
    first_at: typeof o.first_at === 'string' ? o.first_at : null,
    last_at: typeof o.last_at === 'string' ? o.last_at : null,
    final: o.final === true,
    computed_at: str(o.computed_at),
    settings: readSettings(result.settings),
    counts: {
      exceedance_events: num(c.exceedance_events),
      exceeded_readings: num(c.exceeded_readings),
      gaps: num(c.gaps),
      bad_readings: num(c.bad_readings),
      bad_runs: num(c.bad_runs),
    },
    items: readItems(result.items),
    chart: readChart(o.chart),
  };
}

// ------------------------------------------------------------------------------------------------ words

export const CLOUD_QC_STATUS_WORDS: Readonly<Record<CloudQcStationStatus, string>> = {
  online: 'Online',
  offline: 'Offline',
  no_recent: 'No recent readings',
};

export const CLOUD_QC_ITEM_WORDS: Readonly<Record<CloudQcItem['kind'], string>> = {
  exceedance: 'Exceedance',
  gap: 'Data gap',
  bad_data: 'Bad data',
};

/** "13:27:13" in the project time zone. */
export function cloudQcClock(ms: number, offsetH: number): string {
  return localParts(ms, offsetH).hms;
}

/** "Plot 1, Gradient" from stored metric ids. */
export function cloudQcMetricWords(metrics: readonly string[]): string {
  return metrics.map((m) => METRIC_LABEL[m as DiurnalMetric] ?? m).join(', ');
}

/** One line of the counts: "2 exceedance runs · 0 data gaps · 14 bad readings in 3 runs". */
export function cloudQcCountsText(c: CloudQcCounts): string {
  const n = (k: number, w: string): string => `${k} ${w}${k === 1 ? '' : 's'}`;
  return [
    n(c.exceedance_events, 'exceedance run'),
    n(c.gaps, 'data gap'),
    `${n(c.bad_readings, 'bad reading')}${c.bad_runs > 0 ? ` in ${n(c.bad_runs, 'run')}` : ''}`,
  ].join(' · ');
}

/**
 * The settings line under a day ("Plot 1: deviation from chord, 10 nT in 10 min · Plot 2 hidden · edge trim 6 min"),
 * so a reader sees the limits the day was judged by.
 */
export function cloudQcSettingsText(s: CloudQcSettings): string {
  const mode = s.plotMode === 'peakToPeak' ? 'peak to peak' : 'deviation from chord';
  const parts = [`Plot 1: ${mode}, ${s.plot1ThresholdNt} nT in ${s.plot1WindowMin} min`];
  parts.push(
    s.show.plot2 ? `Plot 2: ${s.plot2ThresholdNt} nT in ${s.plot2WindowMin} min` : 'Plot 2 not judged',
  );
  if (s.show.gradient) parts.push(`Gradient: ${s.gradientThresholdNt} nT in ${s.gradientWindowMin} min`);
  if (s.show.pcal) parts.push(`Pcal: ${s.pcalThresholdNt} nT`);
  if (s.show.dor) parts.push(`DoR: ${s.dorThresholdNt} nT in ${s.dorWindowMin} min`);
  parts.push(`first and last ${s.edgeTrimMin} min not judged`);
  return parts.join(' · ');
}

/** Who links a TA Live unit to the project it works on, with dates (decision 788: a manager, when it goes to a job;
 *  tenant admins too). */
export const CLOUD_QC_LINK_ROLES = ['tenant_admin', 'manager'] as const;
/** Who may publish a day to the client: the report publishers (decision 184, `is_report_publisher`). */
export const CLOUD_QC_PUBLISH_ROLES = ['tenant_admin', 'manager', 'data_processor', 'field_crew'] as const;

export function canLinkCloudQcStation(roles: readonly string[]): boolean {
  return roles.some((r) => (CLOUD_QC_LINK_ROLES as readonly string[]).includes(r));
}

export function canPublishCloudQcDay(roles: readonly string[]): boolean {
  return roles.some((r) => (CLOUD_QC_PUBLISH_ROLES as readonly string[]).includes(r));
}

/** A unit's link to a project as `project_base_station` holds it (decision 788). */
export interface StationLinkRow {
  readonly project_id: string;
  readonly station_id: string;
  readonly starts_on: string;
  readonly ends_on: string | null;
}

/** "from 20 Oct 2026", "20 Oct to 4 Nov 2026" for a link's dates. */
export function stationLinkDates(l: Pick<StationLinkRow, 'starts_on' | 'ends_on'>): string {
  const d = (s: string): string => {
    const t = new Date(`${s}T00:00:00Z`);
    return `${t.getUTCDate()} ${t.toLocaleString('en-AU', { month: 'short', timeZone: 'UTC' })} ${t.getUTCFullYear()}`;
  };
  return l.ends_on ? `${d(l.starts_on)} to ${d(l.ends_on)}` : `from ${d(l.starts_on)}`;
}

/** Whether a link is in force on a day (`YYYY-MM-DD`). */
export function stationLinkOn(l: Pick<StationLinkRow, 'starts_on' | 'ends_on'>, day: string): boolean {
  return l.starts_on <= day && (l.ends_on === null || l.ends_on >= day);
}
